import debounce from 'lodash.debounce';
import { ElasticSearchResponse, Hit3, Hit2 } from './elasticsearch-response.model';
import { SearchAutocompleteResult, ProductResult, Options, KeywordResult } from './search-autocomplete.model';

const enum DATA_ATTRIBUTES {
    WRAPPER = 'data-klt-c-search-autocomplete',
    BODY = 'data-klt-c-search-autocomplete-body',
    KEYWORDS_LIST = 'data-klt-c-search-autocomplete-keywords',
    PRODUCT_LIST = 'data-klt-c-search-autocomplete-products',
    PRODUCT_TITLE = 'data-klt-c-product-title',
    PRODUCT_TITLE_TEXT = 'data-klt-c-product-title-text',
    PRODUCT_GRADE_LEVEL = 'data-klt-c-product-grade-level',
    PRODUCT_CONSTITUENT = 'data-klt-c-product-constituent',
    PRODUCT_IMAGE_WRAPPER = 'data-klt-c-product-image-wrapper',
}

const enum IDS {
    TEMPLATE_KEYWORD_ELEMENT = 'template-search-autocomplete-keyword',
    TEMPLATE_PRODUCT_ELEMENT = 'template-search-autocomplete-product',
}

const enum CLASSES {
    INPUT_FIELD_INITALIZED = 'klt-c-search-autocomplete-input-js',
    AUTOCOMPLETE_WRAPPER = 'klt-c-autocomplete-wrapper-js',
    AUTOCOMPLETE_WRAPPER_LOADING = 'klt-c-autocomplete-wrapper-js--loading',
}

let inputWrapperElement: HTMLElement;
let wrapperElement: HTMLDivElement;
let wrapperBodyElement: HTMLDivElement;
let keywordElementTemplate: HTMLTemplateElement;
let productElementTemplate: HTMLTemplateElement;
let inputElement: HTMLInputElement;
let options: Options;
let previousSearchTerm: string;
let abortController: AbortController;
let previousResults: SearchAutocompleteResult;

// TODO: IE11 support check
const supportsHTMLTemplate = 'content' in document.createElement('template');

/**
 * Check if there are results to show
 */
const hasResults = () => {
    return previousResults && (previousResults.products.length > 0 || previousResults.keywords.length > 0);
};

export function init() {
    try {
        wrapperElement = document.querySelector(`[${DATA_ATTRIBUTES.WRAPPER}]`);

        if (!wrapperElement) {
            return;
        }

        const optionsRaw = wrapperElement.getAttribute(DATA_ATTRIBUTES.WRAPPER);
        options = optionsRaw ? (JSON.parse(optionsRaw) as Options) : null;

        if (!options || !options.action) {
            console.error('Search Autocomplete: Not all options set that are required to show search results.');
            return;
        }

        inputElement = options.input ? document.querySelector(`#${options.input}`) : null;

        if (!inputElement) {
            console.error('Search Autocomplete: No input element found to trigger search autocomplete result widget.');
            return;
        }

        // Create a wrapper element for the search field to allow
        // placing a loading indicator over it when the results are being
        // fetched from the API
        inputWrapperElement = document.createElement('div');
        inputWrapperElement.classList.add(CLASSES.AUTOCOMPLETE_WRAPPER);
        inputElement.parentNode.insertBefore(inputWrapperElement, inputElement);
        inputWrapperElement.appendChild(inputElement);

        // We currently don't render the autocomplete on small viewports as the design
        // for it is not yet established
        const vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
        if (vw < 750) {
            return;
        }

        if (!wrapperElement) {
            console.info(
                'Search Autocomplete: No wrapper element for the search results found. Not initalizing search autocompletion.'
            );
            return;
        }

        wrapperBodyElement = wrapperElement.querySelector(`[${DATA_ATTRIBUTES.BODY}]`);

        initTemplates();
        initInputHandlers();
    } catch (e) {
        console.error(`Couldn't initialize search autocomplete module.`, e);
    }
}

/**
 * Puts the results back into the DOM
 */
export function renderResults(results: SearchAutocompleteResult) {
    previousResults = results;

    const productElements = results.products.map((product) => createProductElement(product));
    const keywordElements = results.keywords.map((keyword) => createKeywordElement(keyword));

    const bodyClone = wrapperBodyElement.cloneNode(true) as HTMLDivElement;

    const keywordListElement = bodyClone.querySelector(`[${DATA_ATTRIBUTES.KEYWORDS_LIST}]`);
    const productListElement = bodyClone.querySelector(`[${DATA_ATTRIBUTES.PRODUCT_LIST}]`);

    keywordListElement.textContent = '';
    productListElement.textContent = '';

    productElements.forEach((element) => productListElement.appendChild(element));
    keywordElements.forEach((element) => keywordListElement.appendChild(element));

    wrapperBodyElement.textContent = '';
    wrapperBodyElement.appendChild(bodyClone);

    if (hasResults()) {
        showAutocomplete();
    } else {
        hideAutocomplete();
    }
}

/**
 * Get the template elements from the the DOM and save them in the local variables
 */
function initTemplates() {
    // TODO: Polyfill IE11
    if (supportsHTMLTemplate) {
        // Instantiate the table with the existing HTML tbody
        // and the row with the template
        keywordElementTemplate = wrapperElement.querySelector(`#${IDS.TEMPLATE_KEYWORD_ELEMENT}`);
        productElementTemplate = wrapperElement.querySelector(`#${IDS.TEMPLATE_PRODUCT_ELEMENT}`);
    }
}

/**
 * Add all event handlers on the associated search field
 */
function initInputHandlers() {
    inputElement.addEventListener(
        'input',
        debounce(() => search(inputElement.value), options.debounce)
    );
    inputElement.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') {
            hideAutocomplete();
        }
    });

    inputElement.addEventListener('focus', (e) => {
        if (inputElement.value && inputElement.value.length >= 2 && hasResults()) {
            showAutocomplete();
        }
    });

    inputElement.classList.add(CLASSES.INPUT_FIELD_INITALIZED);
}

/**
 * Trigger search, compile the search results an render them to the DOM
 */
async function search(term: string): Promise<void> {
    if (!term || term.length < 2 || term === previousSearchTerm) {
        return;
    }

    abortController?.abort();

    try {
        abortController = new AbortController();
        showLoadingState();

        const results = await getResults(term);
        const products: SearchAutocompleteResult['products'] = [];
        const keywords: SearchAutocompleteResult['keywords'] = [];

        if (results.products.hits.length > 0) {
            results.products.hits.splice(0, 5).forEach((hit) =>
                products.push({
                    title: hit.highlight?.title?.[0] || hit._source.title,
                    constituent: hit.highlight?.constituent?.[0] || hit._source.constituent,
                    gradeLevel: hit._source.gradeLevel || null,
                    targetUrl: {
                        href: `${options.baseUrlProducts}${hit._source.isbn13 || hit._source.isbn10}`,
                    },
                    image: {
                        src: hit._source.mediaTime
                            ? `${options.baseUrlProductImages}112/161/${
                                  hit._source.isbn13 || hit._source.isbn10
                              }.jpg?hash=${hit._source.mediaTime}`
                            : `${options.baseUrlProductImages}112/161/placeholder.jpg`,
                        alt: 'Vorschaubild',
                    },
                })
            );
        }

        if (results.titlefamilies.hits.length > 0 || results.volumes.hits.length > 0) {
            const hitToKeywordResult = (hit: Hit3 | Hit2) => {
                const text = hit.highlight && hit.highlight.name[0] ? hit.highlight.name[0] : hit._source.name;
                keywords.push({
                    text,
                    href: `${options.baseUrlKeywords}${encodeURIComponent(hit._source.name)}`,
                });
            };

            results.titlefamilies.hits.splice(0, 4).forEach(hitToKeywordResult);
            results.volumes.hits.splice(0, 10).forEach(hitToKeywordResult);
        }

        renderResults({
            products,
            keywords,
        });

        hideLoadingState();
        previousSearchTerm = term;
    } catch (e) {
        if (e.name === 'AbortError') {
            console.info(`Search autocomplete for ${term} aborted as a new search was started.`);
        } else {
            console.error(`Search Autocomplete: Couldn't get results.`, e);
        }

        hideLoadingState();
        return;
    }
}

/**
 * Run the search against the API
 */
async function getResults(term: string): Promise<ElasticSearchResponse> {
    const response = await fetch(`${options.action}${term}`, {
        cache: 'no-cache',
        method: 'GET',
        credentials: 'include',
        signal: abortController.signal,
    });

    return (await response.json()) as ElasticSearchResponse;
}

function showAutocomplete() {
    wrapperElement.style.display = 'block';
    document.body.parentNode.addEventListener('click', handleWrapperOutsideClick);
}

function hideAutocomplete() {
    wrapperElement.style.display = 'none';
    document.body.parentNode.removeEventListener('click', handleWrapperOutsideClick);
}

function showLoadingState() {
    inputWrapperElement.classList.add(CLASSES.AUTOCOMPLETE_WRAPPER_LOADING);
}

function hideLoadingState() {
    inputWrapperElement.classList.remove(CLASSES.AUTOCOMPLETE_WRAPPER_LOADING);
}

/**
 * Creates the product HTML element with the given search results
 */
function createProductElement(data: ProductResult): HTMLElement {
    const clone = productElementTemplate.content.cloneNode(true) as HTMLElement;
    const title = clone.querySelector(`[${DATA_ATTRIBUTES.PRODUCT_TITLE}]`) as HTMLAnchorElement;
    const titleText = clone.querySelector(`[${DATA_ATTRIBUTES.PRODUCT_TITLE_TEXT}]`);
    const gradeLevel = clone.querySelector(`[${DATA_ATTRIBUTES.PRODUCT_GRADE_LEVEL}]`);
    const imageAnchorWrapper = clone.querySelector(`[${DATA_ATTRIBUTES.PRODUCT_IMAGE_WRAPPER}]`) as HTMLAnchorElement;
    const constituent = clone.querySelector(`[${DATA_ATTRIBUTES.PRODUCT_CONSTITUENT}]`);

    titleText.innerHTML = data.title;
    title.href = data.targetUrl.href;
    constituent.innerHTML = data.constituent;
    gradeLevel.textContent = data.gradeLevel;

    const imgElement = imageAnchorWrapper.querySelector('img');

    imgElement.removeAttribute('srcset');
    imgElement.setAttribute('src', data.image.src);
    imgElement.setAttribute('alt', data.image.alt);
    imageAnchorWrapper.href = data.targetUrl.href;

    return clone;
}

/**
 * Creates the keyword HTML element with the given search results
 */
function createKeywordElement(data: KeywordResult): HTMLLIElement {
    const clone = keywordElementTemplate.content.cloneNode(true) as HTMLLIElement;
    const textAnchorWrapper = clone.querySelector('a');

    textAnchorWrapper.innerHTML = data.text;
    textAnchorWrapper.href = data.href;

    return clone;
}

/**
 * Ensure the behaviour of the search results overlay when interacting with the rest
 * of the website
 */
function handleWrapperOutsideClick(e: Event) {
    if (!wrapperElement.contains(e.target as any) && e.target !== inputElement) {
        hideAutocomplete();
    }
}

export default init;
